import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton"]

  connect() {
    this.setupFormValidation()
    this.initializeCaptcha()
  }

  initializeCaptcha() {
    // Ждем загрузки скрипта Yandex SmartCaptcha
    const checkCaptchaLoaded = () => {
      if (typeof window.smartCaptcha !== 'undefined') {
        this.renderCaptcha()
        return true
      }
      return false
    }

    // Проверяем сразу
    if (checkCaptchaLoaded()) return

    // Если скрипт еще не загружен, ждем его загрузки
    const maxAttempts = 50 // 5 секунд максимум
    let attempts = 0

    const interval = setInterval(() => {
      attempts++
      if (checkCaptchaLoaded() || attempts >= maxAttempts) {
        clearInterval(interval)
        if (attempts >= maxAttempts && typeof window.smartCaptcha === 'undefined') {
          console.warn('Yandex SmartCaptcha не загрузился')
          this.showCaptchaError('Ошибка загрузки капчи. Пожалуйста, обновите страницу.')
        }
      }
    }, 100)
  }

  renderCaptcha() {
    const container = document.getElementById('captcha-container')
    if (!container || this.captchaWidgetId) return

    const sitekey = container.getAttribute('data-sitekey')
    if (!sitekey) return

    try {
      this.captchaWidgetId = window.smartCaptcha.render(container, {
        sitekey: sitekey,
        callback: (token) => {
          // Сохраняем токен в скрытое поле
          let tokenInput = document.getElementById('smartcaptcha_token')
          if (!tokenInput) {
            tokenInput = document.createElement('input')
            tokenInput.type = 'hidden'
            tokenInput.id = 'smartcaptcha_token'
            tokenInput.name = 'smartcaptcha_token'
            this.element.appendChild(tokenInput)
          }
          tokenInput.value = token
          this.validateCaptcha()
        },
        'error-callback': () => {
          this.showCaptchaError('Ошибка загрузки капчи. Пожалуйста, обновите страницу.')
        }
      })
    } catch (error) {
      console.error('Ошибка инициализации капчи:', error)
    }
  }

  validateCaptcha() {
    const tokenInput = document.getElementById('smartcaptcha_token')
    const hasToken = tokenInput && tokenInput.value

    if (hasToken) {
      this.clearCaptchaError()
    }

    return !!hasToken
  }

  showCaptchaError(message) {
    const container = document.getElementById('captcha-container')
    if (!container) return

    let errorDiv = container.parentNode.querySelector('.captcha-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.className = 'text-red-500 text-sm mt-2 captcha-error'
      container.parentNode.insertBefore(errorDiv, container.nextSibling)
    }
    errorDiv.textContent = message
  }

  clearCaptchaError() {
    const errorDiv = document.querySelector('.captcha-error')
    if (errorDiv) {
      errorDiv.remove()
    }
  }

  setupFormValidation() {
    // Валидация в реальном времени
    this.element.addEventListener('input', (event) => {
      this.validateField(event.target)
    })

    // Валидация чекбоксов при изменении
    this.element.addEventListener('change', (event) => {
      if (event.target.type === 'checkbox') {
        this.validateCheckbox(event.target)
      } else {
        this.validateField(event.target)
      }
    })

    // Валидация при отправке
    this.element.addEventListener('submit', (event) => {
      if (!this.validateForm()) {
        event.preventDefault()
        this.showErrors()
      } else {
        this.showLoading()
      }
    })
  }

  validateCheckbox(checkbox) {
    // Удаляем предыдущие ошибки
    this.clearFieldError(checkbox)

    // Если чекбокс выбран, валидация проходит успешно и ошибки нет
    // Если не выбран - не показываем ошибку здесь (она появится при submit)
    return checkbox.checked
  }

  validateField(field) {
    const value = field.value.trim()
    const fieldName = field.name
    let isValid = true
    let errorMessage = ''

    // Удаляем предыдущие ошибки
    this.clearFieldError(field)

    switch (fieldName) {
      case 'application[name]':
        if (value.length < 2) {
          isValid = false
          errorMessage = 'Имя должно содержать минимум 2 символа'
        }
        break

      case 'application[email]':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          isValid = false
          errorMessage = 'Введите корректный email адрес'
        }
        break

      case 'application[phone]':
        const phoneRegex = /^\+?[\d\s\-\(\)]+$/
        if (!phoneRegex.test(value) || value.length < 10) {
          isValid = false
          errorMessage = 'Введите корректный номер телефона'
        }
        break

      case 'application[message]':
        if (value.length < 10) {
          isValid = false
          errorMessage = 'Сообщение должно содержать минимум 10 символов'
        }
        break
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage)
    }

    return isValid
  }

  validateForm() {
    const fields = this.element.querySelectorAll('input[required]:not([type="checkbox"]), textarea[required]')
    let isFormValid = true

    fields.forEach(field => {
      if (!this.validateField(field)) {
        isFormValid = false
      }
    })

    // Проверяем согласие на обработку данных
    // Используем более точный селектор для чекбокса (не скрытого input)
    const consentCheckbox = this.element.querySelector('input[type="checkbox"][name="application[consent]"]')
    if (consentCheckbox) {
      if (!consentCheckbox.checked) {
        this.showFieldError(consentCheckbox, 'Необходимо согласие на обработку персональных данных')
        isFormValid = false
      } else {
        // Очищаем ошибку, если чекбокс выбран
        this.clearFieldError(consentCheckbox)
      }
    }

    // Проверяем капчу
    if (!this.validateCaptcha()) {
      this.showCaptchaError('Пожалуйста, пройдите проверку капчи')
      isFormValid = false
    }

    return isFormValid
  }

  showFieldError(field, message) {
    // Удаляем предыдущие ошибки для этого поля сначала
    this.clearFieldError(field)

    // Для чекбоксов используем родительский контейнер
    const container = field.type === 'checkbox' ? field.closest('.flex') || field.parentNode : field.parentNode

    if (!container) return

    const errorDiv = document.createElement('div')
    errorDiv.className = 'text-red-500 text-sm mt-1 field-error'
    errorDiv.setAttribute('data-field-error', field.name || field.id || 'unknown')
    errorDiv.textContent = message

    if (field.type === 'checkbox') {
      // Для чекбоксов добавляем ошибку после контейнера или в конец контейнера
      // Находим родительский элемент с классом flex и добавляем ошибку после него
      const flexContainer = field.closest('.flex')
      if (flexContainer && flexContainer.parentNode) {
        flexContainer.parentNode.insertBefore(errorDiv, flexContainer.nextSibling)
      } else {
        container.appendChild(errorDiv)
      }
    } else {
      field.classList.add('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      field.classList.remove('border-gray-300', 'focus:ring-indigo-500', 'focus:border-indigo-500')
      container.appendChild(errorDiv)
    }
  }

  clearFieldError(field) {
    if (!field) return

    // Для чекбоксов ищем ошибку в родительском контейнере и вокруг него
    if (field.type === 'checkbox') {
      // Находим контейнер с классом flex
      const flexContainer = field.closest('.flex')
      if (flexContainer) {
        // Удаляем все ошибки внутри контейнера
        const errorsInContainer = flexContainer.querySelectorAll('.field-error')
        errorsInContainer.forEach(div => div.remove())

        // Удаляем все ошибки после контейнера (если они были добавлены туда)
        let nextSibling = flexContainer.nextSibling
        while (nextSibling) {
          if (nextSibling.classList && nextSibling.classList.contains('field-error')) {
            const toRemove = nextSibling
            nextSibling = nextSibling.nextSibling
            toRemove.remove()
          } else {
            break
          }
        }

        // Также ищем в родительском элементе
        if (flexContainer.parentNode) {
          const errorsInParent = flexContainer.parentNode.querySelectorAll('.field-error')
          errorsInParent.forEach(div => {
            // Удаляем только если это ошибка для этого чекбокса
            const fieldName = div.getAttribute('data-field-error')
            if (!fieldName || fieldName === 'application[consent]') {
              div.remove()
            }
          })
        }
      } else {
        // Fallback: удаляем из родительского элемента
        if (field.parentNode) {
          const errors = field.parentNode.querySelectorAll('.field-error')
          errors.forEach(div => div.remove())
        }
      }
    } else {
      // Для обычных полей
      const container = field.parentNode
      if (container) {
        const errorDivs = container.querySelectorAll('.field-error')
        errorDivs.forEach(div => {
          const fieldName = div.getAttribute('data-field-error')
          if (!fieldName || fieldName === field.name || fieldName === field.id) {
            div.remove()
          }
        })
      }

      field.classList.remove('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      field.classList.add('border-gray-300', 'focus:ring-indigo-500', 'focus:border-indigo-500')
    }
  }

  showErrors() {
    // Показываем общее сообщение об ошибке
    const errorDiv = document.createElement('div')
    errorDiv.className = 'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4'
    errorDiv.innerHTML = '<strong>Ошибка:</strong> Пожалуйста, исправьте ошибки в форме и попробуйте снова.'

    this.element.insertBefore(errorDiv, this.element.firstChild)

    // Прокручиваем к форме
    this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  showLoading() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = 'Отправка...'
      this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
    }
  }
}
